import { useEffect, useRef, useState } from 'react';

/** Re-renders the calling component every `intervalMs` while `active`. Returns Date.now() at the last tick. */
export function useTicker(active: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const handle = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(handle);
  }, [active, intervalMs]);
  return now;
}

/** Wall-clock time at which `active` last became true, or null while inactive. */
export function useActiveSince(active: boolean): number | null {
  const since = useRef<number | null>(null);
  if (active && since.current === null) since.current = Date.now();
  if (!active) since.current = null;
  return since.current;
}

/**
 * Elapsed recording time that keeps counting between kernel updates: the last
 * reported duration plus the time since it was reported, while recording.
 */
export function useLiveDuration(durationMs: number, running: boolean): number {
  const base = useRef({ durationMs, at: Date.now() });
  if (base.current.durationMs !== durationMs) base.current = { durationMs, at: Date.now() };
  const wasRunning = useRef(running);
  if (wasRunning.current !== running) {
    wasRunning.current = running;
    base.current = { durationMs, at: Date.now() };
  }
  const now = useTicker(running, 500);
  return running ? durationMs + Math.max(0, now - base.current.at) : durationMs;
}
